import { Flex, Icon as ChakraIcon, Text } from '@chakra-ui/react';
import { Star } from 'lucide-react';
import type { ReactNode } from 'react';

/** About V2 sections share the Thmanyah type ramp (see {@link HeroSection}). */
const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', sans-serif";

export type TestimonialVariant = 'light' | 'accent' | 'dark';

/** Per-variant colour tokens (sampled from the Figma design). */
const THEME: Record<TestimonialVariant, { bg: string; text: string; star: string }> = {
  // pale lavender card with dark text and coral stars
  light: { bg: '#F3EDFA', text: '#1B1917', star: '#FF5547' },
  // coral highlighted card with white text and white stars
  accent: { bg: '#EC6757', text: '#FFFFFF', star: '#FFFFFF' },
  // dark espresso card with white text and coral stars
  dark: { bg: '#370D08', text: '#FFFFFF', star: '#FF5547' },
};

export interface TestimonialCardProps {
  /** Review text. */
  quote: ReactNode;
  /** Colour treatment. Defaults to 'light'. */
  variant?: TestimonialVariant;
}

/** Static five-star row — every card shows five filled stars (there is no rating prop). */
const StarRow = ({ color }: { color: string }) => (
  <Flex dir='rtl' justify='flex-start' gap='4px' aria-hidden>
    {Array.from({ length: 5 }, (_, i) => (
      <ChakraIcon
        key={i}
        as={Star}
        boxSize={{ base: '13px', md: '14px' }}
        color={color}
        sx={{ fill: 'currentColor' }}
        stroke='none'
      />
    ))}
  </Flex>
);

/**
 * Testimonial card (TZ-382) for the "ماذا يقولون عنا" section: a fixed five-star row
 * above the review quote. RTL: stars and text align to the start (right). Supports a
 * `light`, `accent` (coral) and `dark` variant.
 */
export const TestimonialCard = ({ quote, variant = 'light' }: TestimonialCardProps) => {
  const theme = THEME[variant];
  const isDark = variant === 'dark';

  return (
    <Flex
      dir='rtl'
      direction='column'
      gap={{ base: '20px', md: '24px' }}
      textAlign='start'
      h='100%'
      borderRadius='24px'
      bg={theme.bg}
      p={isDark ? { base: '24px', md: '36px' } : '24px'}
      justifyContent={'space-between'}
    >
      <StarRow color={theme.star} />

      <Text
        as='p'
        m={0}
        color={theme.text}
        fontFamily={FONT_FAMILY}
        fontWeight={isDark ? '800' : '700'}
        fontSize={isDark ? { base: '18px', md: '22px' } : { base: '15px', md: '16px' }}
        lineHeight={isDark ? '160%' : '175%'}
      >
        {quote}
      </Text>
    </Flex>
  );
};
